// src/components/landing/HeroSection.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Search, MapPin, Star } from "lucide-react";
import { BrandListing } from "../../types/brandlistingsmodel";
import { ListingImage } from "./ListingImage";
import { mediaIcon } from "./mediaIcons";
import { formatINR, mediaTypeLabel } from "../../utils/listingFormat";

function HeroBillboard({ listings }: { listings: BrandListing[] }) {
  const preview = useMemo(() => listings.slice(0, 5), [listings]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (preview.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % preview.length), 3400);
    return () => clearInterval(t);
  }, [preview.length]);

  if (preview.length === 0) {
    return (
      <div className="mx-auto flex aspect-[16/10] w-full max-w-md items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-400">
        Loading featured spaces...
      </div>
    );
  }

  const current = preview[idx];
  const Icon = mediaIcon(current.mediaType);

  return (
    <div className="relative mx-auto w-full max-w-md pb-10 [perspective:1400px]">
      <div className="relative rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.18)] [transform:rotateY(-8deg)_rotateX(3deg)]">
        <div
          key={current.id}
          className="relative aspect-[16/10] w-full animate-[fadein_0.6s_ease] overflow-hidden rounded-xl bg-slate-100"
        >
          <ListingImage photos={current.photos} alt={current.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
          <div className="absolute inset-0 flex flex-col justify-between p-5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-700 backdrop-blur">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                Live preview
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
                <Icon className="h-4 w-4 text-slate-700" />
              </span>
            </div>
            <div>
              <p className="font-display text-2xl leading-none text-white drop-shadow-sm">
                {mediaTypeLabel(current.mediaType)}
              </p>
              <p className="mt-1.5 text-xs text-white/85">
                {current.area}, {current.city}
              </p>
            </div>
          </div>
        </div>
        <div className="absolute -left-1.5 top-1/2 h-8 w-3 -translate-y-1/2 rounded-sm bg-slate-200" />
        <div className="absolute -right-1.5 top-1/2 h-8 w-3 -translate-y-1/2 rounded-sm bg-slate-200" />
      </div>

      <div className="mx-auto h-14 w-2 bg-gradient-to-b from-slate-300 to-slate-100" />
      <div className="mx-auto -mt-1 h-2 w-24 rounded-full bg-slate-300/60 blur-sm" />

      <div className="absolute -bottom-2 -right-2 hidden w-52 rounded-xl border border-slate-200 bg-white p-3 shadow-xl sm:block">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <MapPin className="h-3 w-3 text-[#EB4A2D]" /> {current.city}, {current.state}
        </div>
        <p className="mt-1 text-sm font-semibold text-[#0F172A]">
          {formatINR(current.priceBreakdown.totalPayable)}
          <span className="font-normal text-slate-400"> total</span>
        </p>
        <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-500">
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {current.locationType.name ?? "Verified space"}
        </div>
      </div>
    </div>
  );
}

export function HeroSection({
  listings,
  total,
  cityCount,
  onSearch,
}: {
  listings: BrandListing[];
  total: number;
  cityCount: number;
  onSearch: (q: string) => void;
}) {
  const [q, setQ] = useState("");

  return (
    <section id="top" className="relative overflow-hidden bg-gradient-to-b from-[#F5F8FC] to-white">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[#1D9BD8]/10 blur-3xl" />
      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-16 pt-10 sm:px-8 sm:pt-16 lg:grid-cols-2 lg:items-center lg:pb-24">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 shadow-sm">
            Out-of-home media marketplace
          </span>
          <h1 className="font-display mt-5 text-[2.6rem] leading-[1.05] text-[#0F172A] sm:text-6xl">
            Book billboards.
            <br />
            <span className="bg-gradient-to-r from-[#1D9BD8] to-[#EB4A2D] bg-clip-text text-transparent">
              Skip the phone calls.
            </span>
          </h1>
          <p className="mt-5 max-w-md text-base text-slate-500">
            Adroj lists verified hoardings, unipoles, LED screens, gantries and mall spaces across India — compare,
            add to cart and go live without chasing a single vendor.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSearch(q);
              document.getElementById("listings")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="mt-8 flex max-w-md items-center gap-2 rounded-full border border-slate-200 bg-white p-1.5 pl-4 shadow-sm"
          >
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by city, area or space name"
              className="w-full bg-transparent text-sm text-[#0F172A] placeholder:text-slate-400 focus:outline-none"
            />
            <button
              type="submit"
              className="shrink-0 rounded-full bg-[#EB4A2D] px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
            >
              Search
            </button>
          </form>

          <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
            <div>
              <p className="font-display text-2xl text-[#0F172A]">{total}+</p>
              <p className="text-xs text-slate-500">Active spaces</p>
            </div>
            <div>
              <p className="font-display text-2xl text-[#0F172A]">{cityCount}</p>
              <p className="text-xs text-slate-500">Cities covered</p>
            </div>
            <div>
              <p className="font-display text-2xl text-[#0F172A]">500+</p>
              <p className="text-xs text-slate-500">Brands served</p>
            </div>
          </div>
        </div>

        <HeroBillboard listings={listings} />
      </div>
    </section>
  );
}